import { useCallback, useEffect, useRef, useState } from 'react';

const STORAGE_KEY = 'hakimoLastActivity';
const THROTTLE_MS = 5000;
const POLL_INTERVAL_MS = 15000;
const WARNING_POLL_INTERVAL_MS = 1000;

type IdleState = 'active' | 'warning' | 'timedOut';

interface UseIdleTimeoutOptions {
  timeoutMinutes: number;
  warningSeconds?: number;
  onTimeout: () => void;
  enabled?: boolean;
}

interface UseIdleTimeoutReturn {
  state: IdleState;
  remainingSeconds: number;
  resetTimer: () => void;
}

function getLastActivity(): number {
  const stored = localStorage.getItem(STORAGE_KEY);
  if (stored) {
    const parsed = parseInt(stored, 10);
    if (!isNaN(parsed)) return parsed;
  }
  const now = Date.now();
  localStorage.setItem(STORAGE_KEY, String(now));
  return now;
}

function writeActivity(): void {
  localStorage.setItem(STORAGE_KEY, String(Date.now()));
}

export function useIdleTimeout(
  options: UseIdleTimeoutOptions
): UseIdleTimeoutReturn {
  const {
    timeoutMinutes,
    warningSeconds = 60,
    onTimeout,
    enabled = true,
  } = options;

  const timeoutMs = timeoutMinutes * 60 * 1000;
  const warningMs = warningSeconds * 1000;

  const [state, setState] = useState<IdleState>('active');
  const [remainingSeconds, setRemainingSeconds] = useState(timeoutMinutes * 60);

  const lastThrottleWrite = useRef(0);
  const onTimeoutRef = useRef(onTimeout);
  onTimeoutRef.current = onTimeout;

  const checkIdleStatus = useCallback(() => {
    const lastActivity = getLastActivity();
    const elapsed = Date.now() - lastActivity;
    const remaining = timeoutMs - elapsed;

    if (remaining <= 0) {
      setState('timedOut');
      setRemainingSeconds(0);
      onTimeoutRef.current();
    } else if (remaining <= warningMs) {
      setState('warning');
      setRemainingSeconds(Math.ceil(remaining / 1000));
    } else {
      setState('active');
      setRemainingSeconds(Math.ceil(remaining / 1000));
    }
  }, [timeoutMs, warningMs]);

  const resetTimer = useCallback(() => {
    writeActivity();
    setState('active');
    setRemainingSeconds(timeoutMinutes * 60);
  }, [timeoutMinutes]);

  // Activity listeners
  useEffect(() => {
    if (!enabled) return;

    const handleActivity = () => {
      const now = Date.now();
      if (now - lastThrottleWrite.current >= THROTTLE_MS) {
        lastThrottleWrite.current = now;
        writeActivity();
      }
    };

    const events = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ];
    events.forEach((event) => document.addEventListener(event, handleActivity));

    // Write initial activity timestamp
    writeActivity();

    return () => {
      events.forEach((event) =>
        document.removeEventListener(event, handleActivity)
      );
    };
  }, [enabled]);

  // Polling
  useEffect(() => {
    if (!enabled) return;

    checkIdleStatus();

    const intervalMs =
      state === 'warning' ? WARNING_POLL_INTERVAL_MS : POLL_INTERVAL_MS;
    const intervalId = setInterval(checkIdleStatus, intervalMs);

    return () => clearInterval(intervalId);
  }, [enabled, state, checkIdleStatus]);

  // Visibility change handler
  useEffect(() => {
    if (!enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        checkIdleStatus();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, checkIdleStatus]);

  if (!enabled) {
    return { state: 'active', remainingSeconds: 0, resetTimer };
  }

  return { state, remainingSeconds, resetTimer };
}
